import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { action } from '@/lib/api';
import runLookupAction from '@/screens/apps/redux/actions/runLookupAction';
import readLookupList from '@/screens/apps/redux/actions/readLookupList';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors.js';
import selectors from '@/redux/selectors/selectors';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import parseLookupArguments from './useLookup/parseLookupArguments';

const useAppLookup = ({ id, lookupSettings = {} }) => {
  const [loading, setLoading] = useState(false);
  const [forceReload, setForceReload] = useState(false);
  const [parsedArgs, setParsedArgs] = useState({ args: {}, argsChanged: false });
  const isMountedRef = useRef(true);

  const { lookupLabel, lookupValue, lookupProviderToken, lookupActionId: actionId, type: listType } = lookupSettings;
  const { options: lookupOptions, arguments: lookupArguments } = lookupSettings?.actionInstance || {};

  const app = useSelector(appScreenSelectors.activeApp);
  const appId = useSelector(appScreenSelectors.activeAppId);
  const credentialId = useSelector((state) => appScreenSelectors.activeAppDefaultTestCred(state)?.credentialId);
  const companyId = useSelector((state) => appScreenSelectors.activeAppDefaultTestCred(state)?.companyId);
  const stepOutputs = useSelector(selectors.cookedWorkflowStepOutputs);
  const lookupId = `${appId}-${id}`;
  const lookup = useSelector((state) => state?.workflow?.workflowLookups[lookupId]?.data);
  const lookupOptionsFromState = useSelector((state) => state?.workflow?.workflowLookups[lookupId]?.options);
  const lookupArgs = useSelector((state) => state?.workflow?.workflowLookups[lookupId]?.args);

  const actionInstanceModalState = useSelector((state) => state?.ui?.modals?.actionInstance);
  const isActionInstanceModalOpen = actionInstanceModalState?.open;
  const modalDataSource = actionInstanceModalState?.dataSource;

  const currentModalArguments = useSelector(isActionInstanceModalOpen && modalDataSource && argumentSelectors[modalDataSource] ? argumentSelectors[modalDataSource]?.activeArguments() : () => null);

  const lookupReloadQueue = useSelector((state) => state?.workflow?.lookupReloadQueue || []);

  const isFixed = listType === 'fixed' || !listType;
  const isPreset = !['fixed', 'lookup'].includes(listType);
  const options =
    lookupSettings?.fields?.map((n) => ({ label: n.label, value: n.key })) ||
    Object.entries(lookupSettings?.fixedOptions || {})
      .map(([key, value]) => {
        if (typeof value === 'object' && value !== null && 'label' in value) {
          return { label: value.label, value: key, sequence: typeof value.sequence === 'number' ? value.sequence : Number.MAX_SAFE_INTEGER };
        }
        return { label: value, value: key, sequence: Number.MAX_SAFE_INTEGER };
      })
      .sort((a, b) => (a.sequence || Number.MAX_SAFE_INTEGER) - (b.sequence || Number.MAX_SAFE_INTEGER));

  // Parse arguments asynchronously
  useEffect(() => {
    const parseArgs = async () => {
      try {
        const result = await parseLookupArguments({
          args: lookupArguments,
          previousArgs: lookupArgs,
          actionId,
          app,
          responses: stepOutputs
        });
        if (isMountedRef.current) {
          setParsedArgs(result);
        }
      } catch (error) {
        console.error('Error parsing lookup arguments:', error);
        if (isMountedRef.current) {
          setParsedArgs({ args: {}, argsChanged: false });
        }
      }
    };

    parseArgs();
  }, [lookupArguments, lookupArgs, actionId, app, stepOutputs]);

  const { args, argsChanged } = parsedArgs;

  const formattedLookup = useMemo(() => {
    if (isFixed) {
      return options;
    }
    if (Array.isArray(lookupOptionsFromState)) {
      return lookupOptionsFromState;
    }
    if (Array.isArray(lookup)) {
      return isPreset
        ? lookup
        : lookup?.map((item) => ({
            label: item[lookupLabel],
            value: item[lookupValue],
            required: item.required === 'true' || item.required === true,
            ...(lookupProviderToken && item?.[lookupProviderToken] != null && item?.[lookupProviderToken] !== '' ? { providerToken: item[lookupProviderToken] } : {})
          }));
    }
    return [];
  }, [isFixed, isPreset, lookup, lookupOptionsFromState, options, lookupLabel, lookupValue, lookupProviderToken]);

  const reloadFixedList = useCallback(
    (listType, opts = {}) => {
      setLoading(true);
      readLookupList({
        lookupId,
        listType,
        force: opts.force,
        callback: (data) => {
          if (data) {
            action('SET_WORKFLOW_LOOKUP', { id: lookupId, data });
          }
          setLoading(false);
        }
      });
    },
    [lookupId]
  );

  const reload = useCallback(
    ({ cache, force = false, customArgs }) => {
      if (lookup !== undefined && !force) {
        return;
      }
      if (!['lookup', 'fixed'].includes(listType) && listType) {
        reloadFixedList(listType, { force });
        return;
      }
      if (!actionId) {
        return;
      }
      if (listType === 'fixed') {
        return;
      }
      setLoading(true);

      const useCurrentModalArguments = isActionInstanceModalOpen && currentModalArguments && actionInstanceModalState?.actionId === actionId;
      const argumentsToUse = customArgs || (useCurrentModalArguments ? currentModalArguments : args);
      const lookupSettingsForApi =
        lookupSettings?.useCustomLabelCode && lookupSettings?.lookupLabelMapper
          ? {
              lookupValue: lookupSettings.lookupValue,
              lookupLabel: lookupSettings.lookupLabel,
              lookupProviderToken: lookupSettings.lookupProviderToken,
              useCustomLabelCode: true,
              lookupLabelMapper: lookupSettings.lookupLabelMapper
            }
          : undefined;
      runLookupAction({
        lookupId,
        appId,
        appVersion: app?.version,
        actionId,
        arguments: argumentsToUse,
        options: lookupOptions,
        credentialId,
        companyId,
        lookup: true,
        cache,
        force,
        lookupSettings: lookupSettingsForApi,
        callback: (data) => {
          if (data) {
            const rows = Array.isArray(data) ? data : data?.rows;
            const optionsFromApi = data?.options;
            action('SET_WORKFLOW_LOOKUP', { id: lookupId, data: rows, options: optionsFromApi, args: argumentsToUse });
          }
          setLoading(false);
        }
      });
    },
    [lookup, lookupId, actionId, appId, app?.version, args, credentialId, companyId, listType, reloadFixedList, isActionInstanceModalOpen, currentModalArguments, lookupSettings]
  );

  useEffect(() => {
    if (forceReload) {
      reload({ cache: false, force: true });
      setForceReload(false);
    } else {
      reload({ cache: true });
    }
  }, [actionId, listType, reload, forceReload]);

  useEffect(() => {
    if (argsChanged) {
      setForceReload(true);
      setLoading(true);
    }
  }, [argsChanged]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (lookupReloadQueue.includes(id)) {
      setLoading(true);
      action('SET_WORKFLOW_LOOKUP', { id: lookupId, data: [] });

      const argumentsToUse = isActionInstanceModalOpen && currentModalArguments ? currentModalArguments : args;
      reload({ cache: false, force: true, customArgs: argumentsToUse });

      action('REMOVE_LOOKUP_RELOAD_QUEUE', { id });
    }
  }, [lookupReloadQueue, id, isActionInstanceModalOpen, currentModalArguments, args, reload, lookupId]);

  return {
    lookup: formattedLookup,
    loading,
    reload: () => reload({ cache: false, force: true })
  };
};

export default useAppLookup;
